/**
 * DeliveryOps portal — i18n entry point.
 *
 * Design notes
 * ------------
 * * The portal uses `react-intl` (FormatJS) for ICU MessageFormat support.
 * * The portal ships with *fewer* locales than the mobile driver app by
 *   design — dispatcher/admin personas operate on English only for the
 *   foreseeable future. The infrastructure below is built so adding a
 *   locale is a single pull request (drop `messages/<code>.json`, add an
 *   entry to `SUPPORTED_LOCALES`).
 * * Runtime locale is held in React context via `LocaleProvider` and
 *   persisted in `localStorage`.
 * * All user-facing strings MUST live in `messages/<locale>.json` and be
 *   rendered with `<FormattedMessage>` or `intl.formatMessage`. Date and
 *   number values should go through `web-portal/src/utils/formats.ts`
 *   rather than using `date-fns` helpers directly, so that locale, time
 *   zone and numbering system are applied consistently.
 */

export type SupportedLocale = 'en';

export interface LocaleDescriptor {
  code: SupportedLocale;
  label: string; // label shown in the UI locale switcher
  htmlLang: string; // value for <html lang="">
  direction: 'ltr' | 'rtl';
}

/**
 * The single source of truth for which locales the portal supports.
 * The mobile driver app maintains its own, larger list in
 * `mobile/lib/l10n/locale_controller.dart`.
 */
export const SUPPORTED_LOCALES: readonly LocaleDescriptor[] = [
  { code: 'en', label: 'English', htmlLang: 'en', direction: 'ltr' },
] as const;

export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Resolve which of our supported locales best matches the browser's
 * preferred language list. Always falls back to DEFAULT_LOCALE.
 */
export function resolveLocale(
  candidates: readonly string[] = typeof navigator !== 'undefined'
    ? navigator.languages ?? [navigator.language]
    : [],
): SupportedLocale {
  for (const candidate of candidates) {
    const base = candidate.toLowerCase().split('-')[0] as SupportedLocale;
    if (SUPPORTED_LOCALES.some((l) => l.code === base)) return base;
  }
  return DEFAULT_LOCALE;
}
